import React, { FC, useEffect, useState } from 'react';
import styles from './CreateCampaignForm.module.scss';
import { Checkbox, CloseDark, Input, PlusLight, Select, TreeSelect } from 'esa-ui-library';
import { Form } from '../../../components';
import { useStore } from '../../../../common';
import { PropertyType } from 'esa-ui-library/lib/types';
import { validateForm } from '../MoneyDrop.helper';

interface CreateCampaignFormProps {
	onValidityChange?: (isValid: boolean) => void;
}

const CreateCampaignForm: FC<CreateCampaignFormProps> = ({ onValidityChange }): JSX.Element => {
	const [state, dispatch] = useStore();
	const [prizeErrorIndex, setPrizeErrorIndex] = useState<number | null>(null);
	const prizeRowErrorMessage = 'Number of prizes or prize value cannot be 0 or empty';
	// const { token } = useContext(AuthContext);
	const { campaignForm, formErrors, success, partners, activePartners, games, currencies } = state;
	const statuses = [
		{ label: 'Disabled', value: 'disabled' },
		{ label: 'Enabled', value: 'enabled' },
	];
	const devices = [
		{ label: 'Desktop only', value: '0' },
		{ label: 'Mobile only', value: '1' },
		{ label: 'All devices', value: '9' },
	];

	const onChange = (field: string, val: string) => {
		dispatch('FORM', { field, value: val });
	};

	const onSelect = (field: string, val: PropertyType[] | boolean) => {
		dispatch('FORM', { field, value: val });
	};

	useEffect(() => {
		const errors = validateForm(campaignForm);
		onValidityChange?.(!errors);
	}, [campaignForm]);

	const onPartnersChange = (val: PropertyType[]) => {
		const leafNodes = val.filter((item: any) => !item.options || item.options.length === 0);

		const singleSelection = leafNodes.slice(-1);

		dispatch('PARTNERS_UPDATE', singleSelection);
		dispatch('FORM_CAMPAIGN_PARTNERS', { activePartners: singleSelection });

		onValidityChange?.(singleSelection.length > 0);
	};

	useEffect(() => {
		const providerValue = Array.isArray(campaignForm.provider_id)
			? campaignForm.provider_id[0]?.value
			: campaignForm.provider_id?.value || campaignForm.provider_id;

		if (providerValue && providerValue !== '') {
			dispatch('FORM', { field: 'all_games', value: false });
		} else {
			dispatch('FORM', { field: 'all_games', value: false });
		}
	}, [campaignForm.provider_id]);

	const getProviderValue = (provider: any) => {
		if (!provider) return null;
		if (Array.isArray(provider)) return provider[0]?.value ?? null;
		if (typeof provider === 'object') return provider.value ?? null;
		return provider;
	};

	const providerValue = getProviderValue(campaignForm.provider_id);

	const rawNumberOfPrizes = campaignForm.number_of_prizes ?? [];
	const rawPrizeValues = campaignForm.prize_value ?? [];
	const prizeRowsCount = Math.max(rawNumberOfPrizes.length, rawPrizeValues.length) || 1;
	const normalizePrizeArray = (values: string[]): string[] => {
		const normalized = [...values];
		if (normalized.length < prizeRowsCount) {
			normalized.push(...Array(prizeRowsCount - normalized.length).fill('0'));
		}

		return normalized;
	};
	const numberOfPrizes = normalizePrizeArray(rawNumberOfPrizes);
	const prizeValues = normalizePrizeArray(rawPrizeValues);
	const prizeRows = Array.from({ length: prizeRowsCount }, (_, index) => ({
		numberOfPrizes: numberOfPrizes[index] ?? '0',
		prizeValue: prizeValues[index] ?? '0',
	}));

	const onPrizeFieldChange = (index: number, field: 'number_of_prizes' | 'prize_value', value: string) => {
		const source = field === 'number_of_prizes' ? [...numberOfPrizes] : [...prizeValues];

		source[index] = value;
		dispatch('FORM', { field, value: source });

		if (prizeErrorIndex === index) {
			setPrizeErrorIndex(null);
		}
	};

	const addPrizeRow = async (): Promise<void> => {
		const lastRowIndex = prizeRows.length - 1;
		const lastRow = prizeRows[lastRowIndex];
		const lastRowCount = parseInt(lastRow.numberOfPrizes ?? '0', 10) || 0;
		const lastRowValue = parseInt(lastRow.prizeValue ?? '0', 10) || 0;

		if (!lastRowCount || !lastRowValue) {
			setPrizeErrorIndex(lastRowIndex);
			return;
		}

		const nextNumberOfPrizes = [...numberOfPrizes, '0'];
		const nextPrizeValues = [...prizeValues, '0'];
		await dispatch('FORM', {
			field: 'number_of_prizes',
			value: nextNumberOfPrizes,
		});
		await dispatch('FORM', { field: 'prize_value', value: nextPrizeValues });
		setPrizeErrorIndex(null);
	};

	const removePrizeRow = async (index: number): Promise<void> => {
		const nextNumberOfPrizes = [...numberOfPrizes];
		const nextPrizeValues = [...prizeValues];

		nextNumberOfPrizes.splice(index, 1);
		nextPrizeValues.splice(index, 1);

		if (nextNumberOfPrizes.length === 0 || nextPrizeValues.length === 0) {
			await dispatch('FORM', { field: 'number_of_prizes', value: ['0'] });
			await dispatch('FORM', { field: 'prize_value', value: ['0'] });
		} else {
			await dispatch('FORM', {
				field: 'number_of_prizes',
				value: nextNumberOfPrizes,
			});
			await dispatch('FORM', { field: 'prize_value', value: nextPrizeValues });
		}

		setPrizeErrorIndex(null);
	};

	const prizePool = prizeRows.reduce((total, row) => {
		const prizesCount = parseInt(row.numberOfPrizes ?? '0', 10) || 0;
		const prizeValue = parseInt(row.prizeValue ?? '0', 10) || 0;
		return total + prizesCount * prizeValue;
	}, 0);

	const filteredGameOptions = games
		.filter((game: any) => {
			if (!providerValue) return true;

			if (campaignForm.all_games) return true;

			return game.game_provider_id?.toString() === providerValue.toString();
		})
		.map((game: any) => ({
			label: `${game.game_name} (${game.game_provider_name})`,
			value: game.internal_game_id,
		}));

	return (
		<section>
			<Form success={success}>
				<div className={styles.CreateCampaignForm}>
					<Input
						className={styles.Input}
						type='text'
						label='Campaign name'
						placeholder='Enter name'
						dataId='campaign-name-field'
						required
						onChange={(val) => onChange('campaign_name', val)}
						error={!!formErrors?.campaign_name}
						errorMessage={formErrors?.campaign_name}
					/>
					<Input
						className={styles.Input}
						type='text'
						label='Campaign Description'
						placeholder='Enter description'
						dataId='campaign-description-field'
						required
						onChange={(val) => onChange('campaign_description', val)}
						error={!!formErrors?.campaign_description}
						errorMessage={formErrors?.campaign_description}
					/>
					<Select
						className={styles.Input}
						label='Current Status'
						options={statuses}
						required
						selected={campaignForm.status}
						placeholder='Select status'
						dataId='campaign-status-field'
						onChange={(val) => onSelect('status', val)}
						error={!!formErrors?.campaign_status}
						errorMessage={formErrors?.campaign_status}
					/>
					<TreeSelect
						className={styles.CustomTreeSelect}
						label='Partner'
						options={partners}
						placeholder='Select partners'
						selected={activePartners}
						required
						dataId='partners-field'
						onChange={onPartnersChange}
					/>
					<Input
						className={styles.Input}
						type='datetime-local'
						label='From'
						required
						placeholder='Select start datetime'
						dataId='from-datetime-field'
						onChange={(val) => onChange('from_date', val)}
						error={!!formErrors?.from_date}
						errorMessage={formErrors?.from_date}
					/>
					<Input
						className={styles.Input}
						type='datetime-local'
						label='To'
						required
						placeholder='Select end datetime'
						dataId='to-datetime-field'
						onChange={(val) => onChange('to_date', val)}
						error={!!formErrors?.to_date}
						errorMessage={formErrors?.to_date}
					/>
					<Select
						className={styles.Input}
						label='Device'
						options={devices}
						required
						selected={campaignForm.device}
						placeholder='Select status'
						dataId='campaign-status-field'
						onChange={(val) => onSelect('device', val)}
						error={!!formErrors?.device}
						errorMessage={formErrors?.device}
					/>
					{/*<Checkbox*/}
					{/*	className={styles.Checkbox}*/}
					{/*	checked={campaignForm.allow_api_award}*/}
					{/*	onChange={(val) => onSelect('allow_api_award', val)}*/}
					{/*	label='Allow api award'*/}
					{/*/>*/}
					<Checkbox
						className={styles.Checkbox}
						checked={campaignForm.all_users}
						onChange={(val) => onSelect('all_users', val)}
						label='All users'
					/>
					{/*<Checkbox*/}
					{/*	className={styles.Checkbox}*/}
					{/*	checked={campaignForm.all_games}*/}
					{/*	onChange={(val) => onSelect('all_games', val)}*/}
					{/*	label='All games'*/}
					{/*/>*/}
					{!campaignForm.all_games && (
						<Select
							className={styles.Input}
							search={true}
							label='Games'
							removeable
							options={filteredGameOptions}
							multiple
							placeholder='Select games'
							selected={campaignForm.games}
							dataId='games-field'
							onChange={(val) => onSelect('games', val)}
						/>
					)}
					<Select
						label='Currency'
						options={currencies}
						placeholder='Select currency'
						selected={campaignForm.money_drop_currency}
						dataId='currency-field'
						onChange={(val) => onSelect('money_drop_currency', val)}
						error={!!formErrors?.money_drop_currency}
						errorMessage={formErrors?.money_drop_currency}
					/>
					<div className={styles.PrizeSection}>
						<Input type='number' label='Prize pool' className={styles.Input} value={prizePool.toString()} disabled />
						<div className={styles.PrizeRows}>
							{prizeRows.map((row, index) => {
								const isLastRow = index === prizeRows.length - 1;
								return (
									<div key={`prize-row-${index}`} className={styles.PrizeRow}>
										<Input
											type='number'
											label='Number of prizes:'
											className={styles.Input}
											value={row.numberOfPrizes}
											onChange={(val) => onPrizeFieldChange(index, 'number_of_prizes', val)}
										/>
										<Input
											type='number'
											label='Prize value:'
											className={styles.Input}
											value={row.prizeValue}
											onChange={(val) => onPrizeFieldChange(index, 'prize_value', val)}
										/>
										<button
											type='button'
											className={styles.PrizeAction}
											onClick={() => {
												if (isLastRow) {
													void addPrizeRow();
												} else {
													void removePrizeRow(index);
												}
											}}
											aria-label={isLastRow ? 'Add prize tier' : 'Remove prize tier'}
										>
											{isLastRow ? <PlusLight /> : <CloseDark />}
										</button>
										{prizeErrorIndex === index && <span className={styles.PrizeError}>{prizeRowErrorMessage}</span>}
									</div>
								);
							})}
						</div>
					</div>
					<Input
						className={styles.Input}
						type='text'
						label='Rules'
						placeholder='Enter rules'
						dataId='campaign-rules-field'
						onChange={(val) => onChange('campaign_rules', val)}
						error={!!formErrors?.campaign_rules}
						errorMessage={formErrors?.campaign_rules}
					/>
				</div>
			</Form>
		</section>
	);
};

export { CreateCampaignForm };
